// src/pages/Payment.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { Button } from '../components/Button';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export function Payment() {
  const navigate = useNavigate();
  const [checkoutInfo, setCheckoutInfo] = useState<any>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('checkoutInfo');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCheckoutInfo(parsed);
      } catch (err) {
        console.error('❌ Could not parse sessionStorage.checkoutInfo:', err);
      }
    }
  }, []);

  if (!checkoutInfo?.clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-8 text-gray-600">
        <p className="text-lg max-w-xl">
          Missing payment information. Please return to the <a href="/pricing\" className="underline text-[#004060]">pricing page</a> to begin your subscription.
        </p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: checkoutInfo.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            fontFamily: 'system-ui, sans-serif',
            colorPrimary: '#236caf',
            colorText: '#1a1a1a'
          }
        }
      }}
    >
      <ExpandedCheckoutForm {...checkoutInfo} />
    </Elements>
  );
}

function ExpandedCheckoutForm({
  clientSecret,
  customerId,
  userId,
  priceId,
  promoCode,
  proratedAmount,
  monthlyAmount,
  userName
}: any) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [billingName, setBillingName] = useState(userName || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localPromoCode, setLocalPromoCode] = useState<string>(promoCode ?? '');


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/welcome`
        },
        redirect: 'if_required'
      });

      if (result.error) throw new Error(result.error.message);
      if (result.setupIntent?.status !== 'succeeded') {
        throw new Error(`SetupIntent failed with status: ${result.setupIntent?.status}`);
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      await supabase
        .from('profiles')
        .update({ billing_name: billingName })
        .eq('id', userId);

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/finalize-subscription`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price_id: priceId,
          customer_id: customerId,
          promotion_code_id: localPromoCode,
          billing_name: billingName
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to finalize subscription');
      }

      navigate(`/welcome?amount=${Math.round(proratedAmount)}&monthly=${monthlyAmount.toFixed(2)}&name=${encodeURIComponent(userName)}`);
    } catch (err: any) {
      console.error('❌ Payment error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16 bg-gradient-to-b from-white to-blue-50">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-xl px-8 py-10 space-y-8"
      >
        <div className="space-y-4 text-center">
  <h2 className="text-3xl font-bold text-[#004060]">You're Almost There</h2>
  <p className="text-md text-gray-700">Complete your subscription below to get access immediately.</p>

  <div className="text-left text-lg space-y-1">
  <p>
    <span className="font-medium">Today's Charge:</span>{' '}
    <span className="text-green-700 font-semibold">
      ${(proratedAmount / 100).toFixed(2)}
    </span>
    {localPromoCode && proratedAmount < monthlyAmount * 100 && (
      <span className="text-sm text-gray-500 ml-2">(after promo applied)</span>
    )}
  </p>

  <p>
    <span className="font-medium">Full Monthly Price:</span>{' '}
    {localPromoCode && proratedAmount < monthlyAmount * 100 ? (
      <>
        <span className="line-through text-gray-400">${monthlyAmount.toFixed(2)}</span>
        <span className="ml-2 text-green-700 font-semibold">
          ${monthlyAmount.toFixed(2)} <span className="text-sm font-normal">(after trial)</span>
        </span>
      </>
    ) : (
      <span>${monthlyAmount.toFixed(2)}</span>
    )}
  </p>

  {localPromoCode && proratedAmount < monthlyAmount * 100 && (
    <p className="text-sm text-green-600">
      ✅ You're saving ${((monthlyAmount * 100 - proratedAmount) / 100).toFixed(2)} today.
    </p>
  )}
</div>



</div>


       <div className="space-y-4">
  <label className="block text-sm font-medium text-gray-700">
    Billing Name (optional)
    <input
      type="text"
      value={billingName}
      onChange={(e) => setBillingName(e.target.value)}
      placeholder={`e.g. ${userName}'s Card`}
      className="mt-1 w-full border border-gray-300 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#236caf]"
    />
  </label>

  <details className="mt-4 text-sm text-gray-700">
    <summary className="cursor-pointer hover:underline">Have a promo code?</summary>
    <input
      type="text"
      value={localPromoCode}
      onChange={(e) => setLocalPromoCode(e.target.value)}
      placeholder="Enter promo code"
      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#236caf]"
    />
  </details>

  {localPromoCode && (
    <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 mt-2">
      ✅ Promo code <strong>{localPromoCode}</strong> will be applied to your subscription.
    </div>
  )}

  {error && <div className="text-sm text-red-600">{error}</div>}
</div>

        <PaymentElement />

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? 'Processing…' : 'Start Your Journey'}
        </Button>
      </form>
    </div>
  );
}